import {
  Box,
  Button,
  Container,
  Form,
  Header,
  Icon,
  SpaceBetween,
  Spinner,
  Checkbox,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../app/hook";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";

import { usePostMerchantAlternativeDirectApplicationMutation } from "../../features/api";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { downloadBase64File } from "../../utils/downLoad";
import { formatLabel } from "../../utils/format";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import ErrorTable, {
  MessageItem,
  getMessageItemList,
} from "../UI/table/ErrorTable";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import {
  CANCEL_NOTICE,
  CANCEL_NOTICE_CONTENT,
  CONFIGS,
  CONTENTS,
  LABELS,
  UPDATE_DIFFERENCIAL_INFO,
} from "./constants";
import MultilineText from "../UI/MultilineText";
import { ulFileCheck } from "../../utils/fileControl";
import { UPDATE_DIFFERENCIAL } from "../../configs/constants";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * 加盟店申請（直接）アップロード画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const isApply = !!useHasAuthorityHook(PERMISSIONS.APPLY);
  const isMasterApproval = !!useHasAuthorityHook(PERMISSIONS.MASTER_APPROVAL);
  const isDocomoMasterUser = isDocomoUser && isMasterApproval && isApply;

  // inputRef
  const inputRef = useRef<HTMLInputElement>(null);
  // アップロードファイル
  const [uploadFile, setUploadFile] = useState<File>();
  // エラーテキスト
  const [errorText, setErrorText] = useState("");

  // 加盟店申請（直接代替）API設定
  const [uploadMerchantsDirectlyApplicationFiles] =
    usePostMerchantAlternativeDirectApplicationMutation();

  // スピナー表示処理用
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  // ボタン非活性用
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // エラーメッセージ表示処理用
  const [errorMessageList, setErrorMessageList] = useState<MessageItem[]>([]);

  const [maintenanceFlg, setMaintenanceFlg] = useState(false);

  // 提出用ファイル設定
  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.[0]) {
      return;
    }

    // アップロードされたファイルの拡張子が「.xlsx」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR057" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }

    // 送信するファイル情報を設定
    setUploadFile(files[0]);

    // エラーテキストを設定
    setErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // アップロードボタンイベント
  const uploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    inputRef.current?.click();
  };

  // 申請ボタン押下処理
  const onClickButton = async () => {
    if (!inputCheck()) {
      return;
    }

    if (maintenanceFlg) {
      // メンテナンス時はメンテナンス確認用のダイアログを表示
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.MAINTENANCE_NOTICE,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            confirmExecute();
          },
        })
      );
    } else {
      // それ以外は通常ダイアログ
      confirmExecute();
    }
  };

  // 入力チェック
  const inputCheck = () => {
    let errorFlag = true;

    if (uploadFile === undefined) {
      // ファイル未選択時のエラー表示
      const messageId = "KGRERR054" as keyof Messages;
      // エラーテキストを設定
      setErrorText(getMessage(messageId).label);
      errorFlag = false;
    }

    return errorFlag;
  };

  /**
   * ファイルアップロード実行確認
   */
  const confirmExecute = async () => {
    const fileData = new FormData();
    //  申請書に解約申請を含む場合はtrue
    const cancelIncludeFlag = await ulFileCheck(
      uploadFile,
      UPDATE_DIFFERENCIAL_INFO.ROW_NO,
      UPDATE_DIFFERENCIAL_INFO.COL_NO,
      UPDATE_DIFFERENCIAL_INFO.SHEET_NAME,
      UPDATE_DIFFERENCIAL.CANCELLATION
    );

    // 確認ダイアログ表示メッセージ作成
    let contentMessage = COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM;

    if (uploadFile !== undefined) {
      // アップロードファイル設定処理
      fileData.append(CONFIGS.FILE, uploadFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.ALTERNATIVE_DIRECT_APPLY);
      fileData.append(
        CONFIGS.MAINTENANCE_FLG,
        maintenanceFlg ? "true" : "false"
      );

      //　確認ダイアログ表示メッセージ追記
      contentMessage += formatLabel(CONTENTS.ALTERNATIVE_DIRECT, [
        uploadFile.name,
      ]);
    }

    //　確認ダイアログ表示
    setTimeout(() => {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: (
            <>
              {cancelIncludeFlag && (
                <>
                  <SpaceBetween size="xxxs" direction="horizontal">
                    <Icon name="status-warning" variant="error" />
                    <Box color="text-status-error">{CANCEL_NOTICE}</Box>
                    <Icon name="status-warning" variant="error" />
                  </SpaceBetween>
                  <MultilineText
                    content={CANCEL_NOTICE_CONTENT}
                    boxProps={{ color: "text-status-error" }}
                  />
                </>
              )}
              <MultilineText content={contentMessage} />
            </>
          ),
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            merchantApplication(fileData);
          },
        })
      );
    }, 0);

    /** 同じファイルを連続アップロードできるように処理 */
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  // 確認ボタンOK押下時の処理
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    // エラーテキストを初期化
    setErrorText("");

    // 加盟店情報申請(直接)ファイルAPI呼び出し
    uploadMerchantsDirectlyApplicationFiles(fileData)
      .unwrap()
      .then((res) => {
        // エラー内容を初期化
        setErrorMessageList([]);

        // アップロード後初期化
        setUploadFile(undefined);

        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          if (
            res.errorMessageList !== null &&
            res.errorMessageList.length !== 0
          ) {
            setErrorMessageList(getMessageItemList(res.errorMessageList));
          }
          if (undefined !== res.items && null !== res.items) {
            for (let item of res.items) {
              // ファイルダウンロード処理実行
              downloadBase64File(item);
            }
          }
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  // ref を作成しスクロールさせたい場所にある Element にセット
  const errorRef = createRef<HTMLDivElement>();
  // このコールバックを呼び出して ref.current.scrollIntoView() を呼び出してスクロール
  const scrollToBottomOfList = useCallback(() => {
    // エラー内容にスクロール
    errorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [errorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
  }, [scrollToBottomOfList, errorMessageList]);

  return (
    <Container
      header={
        <Header variant="h2" description="">
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="l">
              {isSpinnerDisplay ? <Spinner size="big" /> : <></>}
              <Button
                iconAlign="right"
                onClick={onClickButton}
                variant="primary"
                disabled={isButtonDisabled}
              >
                {COMMON_LABELS.BTNS.APPLY}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xxl">
            <FileUpload
              inputRef={inputRef}
              label={""}
              name={"file"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION}
              fileName={uploadFile ? uploadFile.name : ""}
              setUploadFile={setUploadFile}
              errorText={errorText}
              setErrorText={setErrorText}
              onChange={onChangeFile}
              onUpload={uploadOnClick}
            />
            {isDocomoMasterUser && (
              <Checkbox
                checked={maintenanceFlg}
                onChange={({ detail: { checked } }) => {
                  setMaintenanceFlg(checked);
                }}
              >
                {LABELS.MAINTENANCE_LABEL}
              </Checkbox>
            )}
          </SpaceBetween>
        </Form>
        <SpaceBetween direction="vertical" size="xxs">
          {errorMessageList.length !== 0 ? (
            <>
              <ErrorTable data={errorMessageList} title={LABELS.ERROR_TITLE} />
              <div id="alternativeDirectError" ref={errorRef} />
            </>
          ) : (
            <></>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};
